/* A handout, on screen and on paper.

   One sheet is one printed page. All three sit at the edge of an A4, and a
   sheet five per cent over spills a nearly empty one — which is why each
   carries a class of its own that does nothing but tighten its spacing.

   The design lives in the app, so the paper is printed from it and never laid
   out a second time.

   OWNED BY ITEM 9 OF docs/plan/00-prestavba-2026-08.md. */

import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Blocks } from "../blocks";
import { useI18n } from "../lib/i18n";
import { useDocs, useHandout, useIndex } from "../lib/hooks";
import { usePortal } from "../lib/portal";
import { build, sources } from "../lib/handout";
import { exampleSheets } from "../lib/handoutExamples";
import { lessonSheets } from "../lib/handoutLesson";
import { LoadFailed } from "./NotFound";

export function Handout() {
  const { id = "" } = useParams();
  const { t, ui } = useI18n();
  const { taxonomy } = usePortal();
  const { data: handout, error, loading } = useHandout(id);
  const { data: docs } = useDocs(handout ? sources(handout) : []);
  const { data: index } = useIndex();

  /* The printable routes are their own thing: no header, no footer, none of the
     chrome that is not part of the sheet being checked. */
  useEffect(() => {
    document.body.classList.add("printing");
    return () => document.body.classList.remove("printing");
  }, []);

  const sheets = useMemo(() => {
    if (!handout || !docs || !index || !taxonomy) return null;
    return build(handout, {
      docs,
      /* Two generators, and the pick says which. `lesson` reaches for a
         document the same way a quoting pick does — through `from`, which is
         what `sources()` gathers — so the lesson is loaded before anything is
         drawn, like every other document a sheet stands on. */
      generate: (pick) => (pick.generate === "lesson"
        ? lessonSheets(pick.from ? docs[pick.from] : undefined, pick, t, ui)
        : exampleSheets(index, taxonomy, pick, t)),
    });
  }, [handout, docs, index, taxonomy, t, ui]);

  if (loading) return null;
  if (error || !handout) return <LoadFailed error={error} />;
  /* Nothing renders until every quoted document is in: a sheet that appeared
     half-built would be printed half-built. */
  if (!sheets) return null;

  return (
    <div className="handout">
      {sheets.map((sheet, i) => (
        <section key={i} className={"sheet" + (sheet.cls ? ` ${sheet.cls}` : "")}>
          {sheet.eyebrow ? <div className="eyebrow">{t(sheet.eyebrow)}</div> : null}
          {sheet.title ? <h2>{t(sheet.title)}</h2> : null}
          {sheet.lead ? <div className="lead">{t(sheet.lead)}</div> : null}
          <Blocks blocks={sheet.blocks} />
          {/* A selector that matched nothing. check-portal.mjs fails the build
              before this can reach paper; on screen it is where the hole is. */}
          {sheet.missing.map((m, j) => <div key={j} className="block-unknown">?{m}</div>)}
        </section>
      ))}
    </div>
  );
}
